import type { SecondaryDimensionValues } from '@revelio/core';
import {
  COMPOSITION_GET_DATA_V3,
  COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3,
  type CompositionHeadcountOnlyV3Query,
  type CompositionV3DataQuery,
  type CompositionV3DataWithActivitiesQuery,
} from '@revelio/data-access';

export const compositionV3DataQueryFor = (
  secondaryDimension: SecondaryDimensionValues | undefined
) =>
  secondaryDimension === 'activities'
    ? COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3
    : COMPOSITION_GET_DATA_V3;

export type CompositionV3GraphqlResponse = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

type CompositionV3HeadcountOnlyGraphqlResponse = NonNullable<
  CompositionHeadcountOnlyV3Query['composition_v3']
>;

/** Payload shape returned by either the full or headcount-only document (overview uses the latter). */
export type CompositionV3AnyGraphqlResponse =
  | CompositionV3GraphqlResponse
  | CompositionV3HeadcountOnlyGraphqlResponse;

type CompositionV3ActivityMetrics = NonNullable<
  CompositionV3DataWithActivitiesQuery['composition_v3']
>['entities'][number]['metrics'];

/** Metrics as selected by the full composition query; use when indexing dynamic keys on a possibly headcount-only payload. */
export type CompositionV3FullMetrics = NonNullable<
  NonNullable<CompositionV3GraphqlResponse['entities']>[number]['metrics']
> &
  Partial<Pick<CompositionV3ActivityMetrics, 'activities'>>;

const METRIC_KEYS_BEYOND_HEADCOUNT_ONLY_V3 = new Set([
  'salary',
  'tenure',
  'growth_rate',
  'hiring_rate',
  'attrition_rate',
  'gender',
  'ethnicity',
  'role',
  'geography',
  'seniority',
  'education',
  'rics',
  'skills',
  'activities',
]);

export const isCompositionV3FullMetrics = (
  m: unknown
): m is CompositionV3FullMetrics => {
  if (m === null || typeof m !== 'object') {
    return false;
  }
  const record = m as Record<string, unknown>;
  if (!Array.isArray(record.headcount)) {
    return false;
  }
  return Object.keys(record).some((k) =>
    METRIC_KEYS_BEYOND_HEADCOUNT_ONLY_V3.has(k)
  );
};
